import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';

import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
    uerCreateWorkOrderActsKC2GroupGql,
    uerUpdateWorkOrderActsKC2GroupGql,
} from '../uerWorkOrderActsKc2ListQueries';

export type TGroupData = {
    headerCheckbox: boolean | 'intermediate' | undefined;
    actKC2Ids: Record<string, boolean>;
    groupName: string | undefined;
};
export const useUerActsKc2GroupSaver = ({
    workOrderId,
    onSuccess,
    actKC2GroupId,
}: {
    workOrderId: number;
    actKC2GroupId: number | null;
    onSuccess: () => void;
}) => {
    const dispatch = useDispatch();

    const [resetCreateData] = useResetStoreData({
        requestId: 'uerActsKc2GroupCreate',
    });
    const [resetUpdateData] = useResetStoreData({
        requestId: 'uerActsKc2GroupUpdate',
    });

    const handleSuccess = useCallback(() => {
        if (!actKC2GroupId) {
            resetCreateData(undefined);
        }
        if (actKC2GroupId) {
            resetUpdateData(undefined);
        }
        onSuccess();
    }, [actKC2GroupId, onSuccess, resetCreateData, resetUpdateData]);

    const [, createGroupStatus, , createGroupRequest] = useRequest({
        requestId: 'bimActsKc2GroupCreate',
        query: uerCreateWorkOrderActsKC2GroupGql,
        withEffect: false,
        dependenciesUpdater: handleSuccess,
    });
    const [, updateGroupStatus, , updateGroupRequest] = useRequest({
        requestId: 'bimActsKc2GroupCreate',
        query: uerUpdateWorkOrderActsKC2GroupGql,
        withEffect: false,
        dependenciesUpdater: handleSuccess,
    });

    const handleSaveGroup = useCallback(
        (formData: TGroupData) => {
            const { actKC2Ids, groupName } = formData;

            if (actKC2Ids && groupName) {
                const ids = Object.keys(actKC2Ids)
                    .filter((id) => actKC2Ids[id])
                    .map((keyIdPairs) => {
                        const [, id] = keyIdPairs.split('_');

                        return {
                            actKC2Id: +id,
                        };
                    });

                if (!actKC2GroupId) {
                    dispatch(
                        createGroupRequest({
                            input: {
                                actKC2Ids: ids,
                                groupName,
                            },
                            workOrderId,
                        }),
                    );
                } else {
                    dispatch(
                        updateGroupRequest({
                            input: {
                                actKC2Ids: ids,
                                groupName,
                            },
                            actKC2GroupId,
                        }),
                    );
                }
            }
        },
        [actKC2GroupId, createGroupRequest, dispatch, updateGroupRequest, workOrderId],
    );

    const isCommit = createGroupStatus === RequestStatus.FETCHING || updateGroupStatus === RequestStatus.FETCHING;

    return {
        handleSaveGroup,
        isCommit,
    };
};
